/**
 * Design tokens — the single definition consumed by `@powwow/ui` (web) and `@powwow/ui-native`
 * (Expo), so a community's branding themes every surface at once and the two cannot drift.
 *
 * Two things here are load-bearing rather than decorative, both from ux.md §2:
 *
 * 1. **Surfaces have densities, not just sizes.** `operational` is the arena — judge scoring,
 *    check-in, the registration desk. Direct August sunlight, untrained volunteers, one-handed
 *    use, possibly regalia. `desk` is the organiser seated in an office weeks earlier, where
 *    density is correct because the work is unhurried. Every size token is resolved through a
 *    density, so a component cannot accidentally render desk-sized controls into an arena.
 *
 * 2. **Contrast is a token value, not a styling choice.** The operational palette targets WCAG
 *    2.2 AAA (7:1) for primary text against its background, because colour is the first thing
 *    sunlight takes.
 */

export type Density = "operational" | "desk";

/* ------------------------------------------------------------------ colour */

/**
 * Neutral ramp. Near-black on near-white rather than the grey-on-white that reads well on a
 * designer's monitor and disappears outdoors. `ink` on `paper` is 16.7:1.
 *
 * The ramp is tinted cool — a trace of the aqua that runs through the brand, rather than a true
 * grey. That is a deliberately cheap effect: the tint costs nothing in contrast (every pairing
 * below still clears AAA by a margin) and it is what keeps a page of white cards from reading as
 * an unstyled document. The tint stays *under* the brand, never beside it, so a community whose
 * own colours are warm does not end up fighting the furniture.
 */
export const neutral = {
  paper: "#FFFFFF",
  surface: "#F2F8FB",
  surfaceSunk: "#E3EFF5",
  line: "#D3E4EC",
  lineStrong: "#AFC9D6",
  muted: "#4F6875",
  ink: "#08202D",
} as const;

/**
 * Semantic colours. Each `*Ink` is the text colour that meets AAA on `paper`; each `*Surface`
 * is a fill light enough to carry `ink` at AAA. Nothing in the system encodes meaning in
 * colour alone — these always accompany a word or an icon.
 *
 * `accent` is the highlight — a selected tile, an unread count, a text selection — so it is aqua,
 * with the rest of the palette. The three that report a *condition* stay where a reader already
 * expects them: success green, warning amber, danger red. Retinting those to match the furniture
 * would be the one place where looking consistent costs something real.
 *
 * `pending` sits deliberately away from `accent` on the wheel — indigo rather than another
 * teal — because "highlighted" and "not saved yet" appearing in the same view must not look like
 * the same thing at a glance.
 */
export const semantic = {
  accentInk: "#0A5566",
  accentSurface: "#D9F0F5",
  accentStrong: "#07414F",

  successInk: "#12583A",
  successSurface: "#D8F0E2",

  warningInk: "#6E4300",
  warningSurface: "#FAEACF",

  dangerInk: "#8C1B21",
  dangerSurface: "#F9DEDF",

  /** Reserved for the sync states in `@powwow/sync`, which are always shown with their words. */
  pendingInk: "#3A4C77",
  pendingSurface: "#E8ECF8",
} as const;

/**
 * The default brand. A community overrides these through `communities.branding_json`; every
 * other token stays fixed, because a community may choose its colours but may not choose to
 * make a judge's screen unreadable.
 *
 * Deep water rather than near-black. `primary` is as bright as AAA allows and not one step
 * brighter: at 7.9:1 against `primaryContrast` it is unmistakably aqua rather than another dark
 * slate, and it still carries its own contrast colour, which is what lets one variable be both
 * the button fill and the active-navigation fill without a second token for each.
 *
 * The ceiling is real. `primaryContrast` is `surface`, not `paper`, so the pairing has to clear
 * 7:1 against the *darker* of the two backgrounds this ever lands on — which puts anything
 * lighter than roughly `#0D5D73` under the line, and the difference between 7.9 and 6.9 is the
 * difference between a button a judge can read at noon and one they cannot.
 */
export const defaultBrand = {
  primary: "#0C5468",
  primaryContrast: neutral.surface,
  secondary: semantic.accentInk,
  secondaryContrast: neutral.paper,
} as const;

/**
 * Widened deliberately. `defaultBrand` is `as const`, so its inferred type is the four literal
 * hex strings — which would make "a brand" mean "exactly the default brand" and reject every
 * community's own colours.
 */
export interface Brand {
  primary: string;
  primaryContrast: string;
  secondary: string;
  secondaryContrast: string;
}

/* ---------------------------------------------------------------- platform */

/**
 * The platform's own identity — who owns the software, as distinct from the community whose event
 * it happens to be running. A community brands the surface (`defaultBrand` above, overridden
 * through `branding_json`); this attribution stays the platform's throughout.
 *
 * It lives here, beside the default brand, so that both apps read the same value and a footer
 * credit cannot drift between the public site and the staff app.
 */
export const platform = {
  /**
   * The owner of the software, and the sole copyright holder. The product belongs to Great Lakes
   * IT Inc.; no other name is asserted alongside it, so every surface that credits the platform
   * credits exactly this one.
   */
  owner: {
    name: "Great Lakes IT Inc.",
    url: "https://greatlakesit.ca",
    /**
     * The full lockup, trimmed to the artwork and cut at 3× a 40px display height. Both apps
     * serve it from the same path so this constant stays true wherever it is read; only the
     * staff app's front door actually shows it today.
     *
     * The source export sits on white and is used as it is, on a white chip, rather than keyed
     * to transparency.
     */
    logo: "/brand/partners/great-lakes-it.png",
  },
} as const;

/**
 * The copyright line as plain text, e.g. `© 2026 Great Lakes IT Inc.` — for print, exports and
 * anywhere a link cannot go. Surfaces that can link render from `platform.owner` instead.
 *
 * The year is passed in rather than read from the clock, so a server-rendered page and a
 * client-rendered one cannot disagree across a New Year, and the caller decides it once.
 */
export function copyrightNotice(year: number): string {
  return `© ${year} ${platform.owner.name}`;
}

/* -------------------------------------------------------------------- type */

/**
 * Type scale in px. The operational scale starts at 18 and steps hard; `display` is the dancer
 * number, which a judge reads at arm's length in sunlight.
 */
export const typeScale = {
  operational: {
    caption: 15,
    body: 18,
    bodyLarge: 21,
    title: 26,
    heading: 32,
    display: 64,
  },
  desk: {
    caption: 12,
    body: 15,
    bodyLarge: 17,
    title: 20,
    heading: 26,
    display: 40,
  },
} as const satisfies Record<Density, Record<string, number>>;

export const lineHeight = {
  tight: 1.15,
  snug: 1.3,
  normal: 1.5,
} as const;

export const fontWeight = {
  regular: "400",
  medium: "500",
  semibold: "600",
  bold: "700",
} as const;

/**
 * A system stack. No web font is loaded on operational surfaces: a judge on venue wifi should
 * never see unstyled or invisible text while a font request hangs.
 */
export const fontFamily = {
  sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
} as const;

/* ------------------------------------------------------ spacing and shape */

/** 4 px base. Operational surfaces use the larger end of this ramp, never the smaller. */
export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
} as const;

export const radius = {
  none: 0,
  sm: 4,
  md: 10,
  lg: 14,
  xl: 22,
  full: 9999,
} as const;

/**
 * Elevation, as data rather than as a CSS string.
 *
 * A shadow is the one piece of styling that was being written as a literal everywhere — eight
 * files each carrying their own `rgba(11, 20, 36, 0.05)`, which is the old ink, spelled out by
 * hand, in a system whose entire premise is that no colour is spelled out by hand. Changing the
 * palette left every one of them casting a warm shadow onto a cool page.
 *
 * So the layers are described numerically and each consumer composes them: `css.ts` builds
 * `box-shadow` strings tinted with `--pw-neutral-ink`, and `ui-native` can map the same numbers
 * onto React Native's `shadowOffset`/`shadowRadius`/`shadowOpacity` without parsing anything.
 * `alpha` is the tint's share of the ink colour, not of black.
 */
export interface ShadowLayer {
  /** Vertical offset in px. Light comes from above, so this is never negative. */
  y: number;
  blur: number;
  spread: number;
  /** 0–1, applied to the ink colour rather than to black. */
  alpha: number;
}

export const elevation = {
  /** A hairline lift. A resting card, a quiet control. */
  sm: [{ y: 1, blur: 2, spread: 0, alpha: 0.05 }],
  /** Off the page. A card that can be clicked, a raised button. */
  md: [
    { y: 2, blur: 6, spread: -2, alpha: 0.08 },
    { y: 1, blur: 2, spread: -1, alpha: 0.05 },
  ],
  /** Floating. A hovered row, the primary action, a sticky bar with content under it. */
  lg: [{ y: 10, blur: 26, spread: -12, alpha: 0.22 }],
  /** Over everything. Dialogs only. */
  xl: [{ y: 22, blur: 52, spread: -20, alpha: 0.3 }],
} as const satisfies Record<string, readonly ShadowLayer[]>;

/**
 * Minimum touch target, in px, per ux.md §2. 56 in the arena; 44 — the WCAG 2.2 AA floor —
 * at the organiser's desk. Components read this rather than hard-coding a height.
 */
export const touchTarget = {
  operational: 56,
  desk: 44,
} as const satisfies Record<Density, number>;

/** Gap between two controls whose confusion would matter. Wider in the arena, deliberately. */
export const controlGap = {
  operational: space[4],
  desk: space[2],
} as const satisfies Record<Density, number>;

/**
 * Motion is functional only — confirmation, state change, orientation. Everything respects
 * `prefers-reduced-motion`, which the consuming layer honours by collapsing these to 0.
 */
export const duration = {
  instant: 0,
  fast: 120,
  normal: 200,
  /** How long an Undo affordance stays after a check-in or a score (ux.md §3.4). */
  undoWindow: 8000,
} as const;

export const focusRing = {
  width: 3,
  offset: 2,
  color: semantic.accentStrong,
} as const;

export const tokens = {
  neutral,
  semantic,
  defaultBrand,
  typeScale,
  lineHeight,
  fontWeight,
  fontFamily,
  space,
  radius,
  elevation,
  touchTarget,
  controlGap,
  duration,
  focusRing,
} as const;

export type Tokens = typeof tokens;
